import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  ArrowLeft,
  RotateCcw,
  User,
  Receipt,
  MapPin,
  Calendar,
  FileText,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import toast from 'react-hot-toast';

const currency = (n) =>
  Number(n || 0).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function DevolucionDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ret, setRet] = useState(null);
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (id && profile?.organization_id) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, profile?.organization_id]);

  async function fetchData() {
    try {
      setLoading(true);
      const [retRes, itemsRes] = await Promise.all([
        supabase
          .from('returns')
          .select('*, clients(full_name), sitios(nombre), sales(id, numero_factura, fecha_venta, total)')
          .eq('id', id)
          .single(),
        supabase
          .from('return_items')
          .select('*, products(name)')
          .eq('return_id', id),
      ]);

      if (retRes.error) throw retRes.error;
      setRet(retRes.data);
      setItems(itemsRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Devolución no encontrada');
      navigate('/sistema/devoluciones');
    } finally {
      setLoading(false);
    }
  }

  if (loading)
    return (
      <div className="h-screen flex items-center justify-center bg-[#F2F9FF]">
        <Loader2 className="animate-spin text-slate-300" />
      </div>
    );

  if (!ret) return null;

  return (
    <div className="min-h-screen bg-[#F2F9FF] p-4 md:p-8 font-sans text-slate-700">
      {/* HEADER */}
      <div className="max-w-5xl mx-auto mb-6 flex items-center justify-between gap-3">
        <button
          onClick={() => navigate('/sistema/devoluciones')}
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-600 font-black uppercase text-[10px] tracking-widest hover:bg-slate-50 transition-all shadow-sm"
        >
          <ArrowLeft size={14} /> Volver
        </button>
        {ret.sale_id && (
          <button
            onClick={() => navigate(`/sistema/ventas/detalle/${ret.sale_id}`)}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-[#51A7FD] font-black uppercase text-[10px] tracking-widest hover:bg-blue-50 transition-all shadow-sm"
          >
            Venta original <ExternalLink size={12} />
          </button>
        )}
      </div>

      {/* CARD */}
      <div className="bg-white max-w-5xl mx-auto rounded-xl shadow-lg border border-slate-200 overflow-hidden">
        {/* Cabecera */}
        <div className="p-6 border-b border-slate-100 flex justify-between items-start">
          <div>
            <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight mb-1 flex items-center gap-2">
              <RotateCcw size={18} className="text-[#51A7FD]" /> Detalle Devolución
            </h1>
            <div className="flex gap-4 text-[10px] font-bold text-slate-400 uppercase tracking-tight">
              <p>
                ID: <span className="text-slate-600">{String(ret.id).slice(0, 8)}</span>
              </p>
              <p>
                Fecha: <span className="text-slate-600">{ret.fecha_devolucion}</span>
              </p>
            </div>
          </div>
          <span
            className={`px-4 py-1 rounded-full text-[9px] font-black uppercase tracking-widest ${
              ret.active ? 'bg-[#5CB85C] text-white' : 'bg-slate-400 text-white'
            }`}
          >
            {ret.active ? 'Procesada' : 'Anulada'}
          </span>
        </div>

        {/* Info */}
        <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20">
            <User size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                Cliente
              </h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">
                {ret.clients?.full_name || 'Consumidor Final'}
              </p>
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20">
            <Receipt size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                Venta origen
              </h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">
                {ret.sales?.numero_factura || (ret.sale_id ? `#${String(ret.sale_id).slice(0, 8)}` : '—')}
              </p>
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20">
            <MapPin size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                Sucursal
              </h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">
                {ret.sitios?.nombre || '—'}
              </p>
            </div>
          </div>

          <div className="border border-slate-100 rounded-xl p-3.5 flex gap-3 items-center bg-slate-50/20">
            <Calendar size={16} className="text-slate-300" />
            <div className="flex-1">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest leading-none mb-1">
                Método de reintegro
              </h4>
              <p className="text-xs font-black uppercase text-slate-700 leading-none">
                {ret.payment_method || '—'}
              </p>
            </div>
          </div>
        </div>

        {(ret.reason || ret.observations) && (
          <div className="px-5 pb-5">
            <div className="border border-slate-100 rounded-xl p-3.5 bg-slate-50/20">
              <h4 className="text-[9px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-1">
                <FileText size={11} /> Motivo y observaciones
              </h4>
              {ret.reason && <p className="text-xs font-bold text-slate-700 mb-1">{ret.reason}</p>}
              {ret.observations && (
                <p className="text-[11px] text-slate-500 italic leading-relaxed">{ret.observations}</p>
              )}
            </div>
          </div>
        )}

        {/* Items */}
        <div className="px-5 pb-5">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-y border-slate-100 bg-slate-50/50 text-[9px] font-black uppercase text-slate-400 tracking-wider">
                <th className="py-2 px-2">Producto</th>
                <th className="py-2 px-2 text-center w-24">Cantidad</th>
                <th className="py-2 px-2 text-right w-40">Precio unidad</th>
                <th className="py-2 px-2 text-right w-40">Subtotal</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.map((it) => (
                <tr key={it.id} className="text-[11px] font-bold text-slate-600">
                  <td className="py-3 px-2 uppercase tracking-tight">
                    {it.product_name_snapshot || it.products?.name || 'Producto'}
                  </td>
                  <td className="py-3 px-2 text-center">{it.quantity}</td>
                  <td className="py-3 px-2 text-right">$ {currency(it.unit_price)}</td>
                  <td className="py-3 px-2 text-right font-black text-slate-800">
                    $ {currency(it.subtotal ?? it.quantity * it.unit_price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Footer */}
        <div className="bg-[#1B252E] text-white p-4 flex justify-between items-center">
          <p className="text-[9px] font-bold text-slate-400 uppercase tracking-tight italic leading-none">
            Reintegro de mercadería y dinero al cliente.
          </p>
          <div className="flex items-center gap-4">
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-slate-400">Total reintegrado:</span>
            <span className="text-2xl font-black tracking-tighter leading-none text-white">
              $ {currency(ret.total)}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DevolucionDetalle;
